import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Clock, MessageSquare, Globe, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';
import api from '../services/api';

const Contact = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        interest: 'Website Development',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const res = await api.post('/queries', {
                fullName: formData.fullName,
                email: formData.email,
                projectInterest: formData.interest,
                message: formData.message
            });

            if (res.data.success) {
                toast.success('Inquiry sent successfully! We\'ll get back to you soon.', {
                    duration: 5000,
                    style: {
                        background: '#1e293b',
                        color: '#fff',
                        borderRadius: '1rem',
                    },
                    iconTheme: {
                        primary: '#3b82f6',
                        secondary: '#fff',
                    },
                });
                
                setIsSuccess(true);
                setFormData({
                    fullName: '',
                    email: '',
                    interest: 'Website Development',
                    message: ''
                });
                
                // Reset success state after some time
                setTimeout(() => setIsSuccess(false), 5000);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white dark:bg-dark transition-colors duration-500 min-h-screen">
            <SEO
                title="Contact Us | Hire the Best Tech Team - MehareTech"
                description="Have a project in mind? Contact MehareTech today for a free expert consultation and quote. We specialize in building future-proof digital products and high-performance engineering."
                keywords="contact MehareTech, hire web developers, mobile app development consultation, IT outsourcing India, custom software quote, hire React developers, tech partnership"
            />
            {/* Page Header */}
            <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 bg-slate-900 dark:bg-[#02000c] overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-0 left-0 w-full h-full bg-primary/10 blur-[100px]"></div>
                </div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-primary font-bold uppercase tracking-[0.2em] text-sm mb-4 block"
                    >
                        Communication Hub
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-6xl font-black text-white mb-4 md:mb-6"
                    >
                        Let's Ignite Your <span className="text-primary-light">Vision</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto"
                    >
                        Have a project in mind? We're ready to engineer the solution.
                    </motion.p>
                </div>
            </section>

            {/* Contact Section */}
            <section className="py-12 md:py-24 relative overflow-hidden">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20">
                        {/* Info Column */}
                        <div className="space-y-8 md:space-y-12">
                            <div>
                                <h2 className="text-2xl md:text-4xl font-black !text-black mb-4 md:mb-6 tracking-tight">Connect With Excellence</h2>
                                <p className="!text-slate-700 text-sm md:text-base leading-relaxed font-medium">
                                    Our team of experts is ready to discuss your business requirements and provide the most effective technical roadmap.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 md:gap-8">
                                {[
                                    { icon: Phone, title: 'Call Us', value: '+91 8708604830', color: 'bg-primary/10' },
                                    { icon: Mail, title: 'Email Us', value: 'meharetech@gmail.com', color: 'bg-primary/10' },
                                    { icon: Clock, title: 'Working Hours', value: 'Mon - Sat, 10:00 - 19:00', color: 'bg-primary/10' },
                                    { icon: MapPin, title: 'Location', value: 'Haryana, India', color: 'bg-primary/10' }
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-4 group cursor-default p-3 md:p-4 bg-white rounded-xl border border-slate-200 shadow-sm transition-all hover:shadow-md">
                                        <div className={`w-10 h-10 md:w-12 md:h-12 ${item.color} rounded-lg md:rounded-xl flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300`}>
                                            <item.icon className="w-5 h-5 md:w-6 md:h-6" />
                                        </div>
                                        <div className="relative z-10">
                                            <h3 style={{ color: 'black' }} className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.2em] mb-0.5">{item.title}</h3>
                                            <p style={{ color: 'black' }} className="text-base md:text-lg font-bold leading-tight select-all">{item.value}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="p-6 md:p-10 bg-slate-900 dark:bg-dark-accent/40 rounded-[1.5rem] md:rounded-[2.5rem] text-white relative overflow-hidden backdrop-blur-xl border border-white/5">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full blur-3xl"></div>
                                <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 relative z-10 flex items-center gap-3">
                                    <Globe className="text-primary" /> Global Collaboration
                                </h3>
                                <p className="text-slate-400 text-xs md:text-sm relative z-10 leading-relaxed">
                                    We work with clients worldwide, spanning multiple time zones and industries. Distance is never a barrier to innovation.
                                </p>
                            </div>
                        </div>

                        {/* Form Column */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="bg-white dark:bg-dark-accent/10 p-6 md:p-12 rounded-[2rem] md:rounded-[3rem] shadow-2xl shadow-slate-200 dark:shadow-primary/5 border border-slate-100 dark:border-white/5 relative backdrop-blur-2xl"
                        >
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    <div className="space-y-2">
                                        <label htmlFor="fullName" style={{ color: 'black' }} className="text-[11px] font-bold ml-1 uppercase tracking-widest">Full Name</label>
                                        <input 
                                            id="fullName"
                                            name="fullName"
                                            type="text" 
                                            required
                                            value={formData.fullName}
                                            onChange={handleChange}
                                            style={{ color: 'black' }}
                                            className="w-full px-4 py-3 md:px-5 md:py-4 bg-white border border-slate-200 rounded-lg md:rounded-xl outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 font-bold text-base transition-all placeholder:text-slate-400" 
                                            placeholder="John Doe" 
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label htmlFor="email" style={{ color: 'black' }} className="text-[11px] font-bold ml-1 uppercase tracking-widest">Email Address</label>
                                        <input 
                                            id="email"
                                            name="email"
                                            type="email" 
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            style={{ color: 'black' }}
                                            className="w-full px-4 py-3 md:px-5 md:py-4 bg-white border border-slate-200 rounded-lg md:rounded-xl outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 font-bold text-base transition-all placeholder:text-slate-400" 
                                            placeholder="john@example.com" 
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="interest" style={{ color: 'black' }} className="text-[11px] font-bold ml-1 uppercase tracking-widest">Project Interest</label>
                                    <select 
                                        id="interest"
                                        name="interest"
                                        value={formData.interest}
                                        onChange={handleChange}
                                        style={{ color: 'black' }}
                                        className="w-full px-4 py-3 md:px-5 md:py-4 bg-white border border-slate-200 rounded-lg md:rounded-xl outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 font-bold text-base transition-all cursor-pointer"
                                    >
                                        <option value="Website Development">Website Development</option>
                                        <option value="Android Development">Android Development</option>
                                        <option value="Custom Software">Custom Software</option>
                                        <option value="SEO">SEO Services</option>
                                        <option value="Digital Marketing">Digital Marketing</option>
                                        <option value="Other">Other Inquiry</option>
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="message" style={{ color: 'black' }} className="text-[11px] font-bold ml-1 uppercase tracking-widest">Message Detail</label>
                                    <textarea 
                                        id="message"
                                        name="message"
                                        rows="4" 
                                        required
                                        value={formData.message}
                                        onChange={handleChange}
                                        style={{ color: 'black' }}
                                        className="w-full px-4 py-3 md:px-5 md:py-4 bg-white border border-slate-200 rounded-lg md:rounded-xl outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 font-bold text-base transition-all resize-none placeholder:text-slate-400" 
                                        placeholder="Tell us about your project..."
                                    ></textarea>
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={isSubmitting}
                                    className={`w-full py-4 md:py-6 bg-primary text-white font-black rounded-xl md:rounded-[2rem] shadow-xl shadow-primary/30 hover:bg-primary-dark transition-all flex items-center justify-center gap-3 md:gap-4 active:scale-95 group purple-glow text-sm md:text-base ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center gap-2">
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                                            />
                                            Processing...
                                        </span>
                                    ) : (
                                        <>
                                            Launch Inquiry <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>

                            <AnimatePresence>
                                {isSuccess && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center gap-3 text-green-600 dark:text-green-400 text-sm font-bold justify-center"
                                    >
                                        <CheckCircle2 size={18} /> Message received! We'll reach out shortly.
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="mt-10 pt-10 border-t border-slate-100 dark:border-white/5 flex items-center justify-center gap-3 text-slate-400 dark:text-slate-500 text-sm font-medium">
                                <MessageSquare size={18} /> typically responds in 2 hours
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
